import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnread";
import ContactsIcon from "@mui/icons-material/Contacts";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StyleIcon from "@mui/icons-material/Style";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import DashboardService from "../../services/DashboardService";
import DashboardSection from "./DashboardSection";
import StatCard from "./StatCard";
import MessageVolumeBars from "./MessageVolumeBars";
import BroadcastInsightsCard from "./BroadcastInsightsCard";
import IntegrationStatusCard from "./IntegrationStatusCard";
import { DASHBOARD_PERIODS, DEFAULT_DASHBOARD_PERIOD } from "./dashboardPeriods";
import { THEME_PRIMARY, THEME_PRIMARY_LIGHT, dash } from "./dashboardTokens";
import ServicesService from "../../services/ServicesService";

const BUSINESS_GOALS = [
  {
    id: "sell_products",
    label: "Sell products",
    helper: "Catalog, orders, payments",
    path: "/automation/chatbots",
    Icon: ShoppingBagOutlinedIcon,
  },
  {
    id: "appointments",
    label: "Take appointments",
    helper: "Slots, staff, reminders",
    path: "/appointments",
    Icon: EventAvailableOutlinedIcon,
  },
  {
    id: "tickets",
    label: "Tickets / reservations",
    helper: "Routes, seats, capacity",
    path: "/tickets",
    Icon: ConfirmationNumberOutlinedIcon,
  },
  {
    id: "support",
    label: "Customer support",
    helper: "Inbox and routing",
    path: "/teamchat",
    Icon: SupportAgentOutlinedIcon,
  },
  {
    id: "broadcasts",
    label: "Send broadcasts",
    helper: "Campaigns and updates",
    path: "/broadcast/messages",
    Icon: CampaignOutlinedIcon,
  },
  {
    id: "leads",
    label: "Collect leads",
    helper: "Questions and follow-up",
    path: "/automation/chatbots",
    Icon: PersonAddAltOutlinedIcon,
  },
];

const BUSINESS_CATEGORIES = [
  "Ecommerce / retail",
  "Healthcare / clinic",
  "Salon / services",
  "Travel / transport",
  "Events / classes",
  "Education",
  "Restaurant / food",
  "Real estate",
  "Customer support",
  "Other",
];

const LANGUAGES = ["English", "Hindi", "Tamil", "Malayalam", "Kannada", "Telugu", "Arabic", "Other"];

const emptyBusinessSetup = {
  goals: [],
  businessName: "",
  businessPhone: "",
  businessEmail: "",
  category: "",
  country: "India",
  language: "English",
  paymentNeeded: "",
  productType: "digital-goods",
  businessAddressLine1: "",
  businessAddressLine2: "",
  businessCity: "",
  businessState: "",
  businessPincode: "",
};

function DashboardHero({ period, onPeriodChange, loading = false, }) {
  return (
    <Box
      component="header"
      sx={{
        mb: { xs: 2.5, md: 3 },
        pb: { xs: 2, md: 2.5 },
        borderBottom: `1px solid ${dash.border}`,
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "flex-start", lg: "center" }}
        justifyContent="space-between"
        gap={2}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.375rem", sm: "1.5rem" },
                letterSpacing: "-0.02em",
                color: "text.primary",
                lineHeight: 1.25,
              }}
            >
              Dashboard
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              color: dash.textMuted,
              fontSize: 13,
              lineHeight: 1.5,
              maxWidth: 520,
            }}
          >
            WhatsApp metrics for your workspace
          </Typography>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ width: { xs: "100%", lg: "auto" } }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 176 },
              flex: { xs: 1, sm: "0 0 auto" },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: dash.surface,
              },
            }}
          >
            <InputLabel id="dashboard-period-label">Date range</InputLabel>
            <Select
              labelId="dashboard-period-label"
              label="Date range"
              value={period}
              onChange={(e) => onPeriodChange(e.target.value)}
            >
              {DASHBOARD_PERIODS.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    </Box>
  );
}
export default function DashboardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(DEFAULT_DASHBOARD_PERIOD);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceSettings, setServiceSettings] = useState(null);
  const [businessSetup, setBusinessSetup] = useState(emptyBusinessSetup);
  const [setupOpen, setSetupOpen] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const summary = await DashboardService.fetchSummary(period);
      setData(summary);
      setError(null);
    } catch (e) {
      setError(e?.message || "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const settings = await ServicesService.getServiceSettings();
        if (!mounted) return;
        const savedSetup = settings?.allSettings?.business_setup || {};
        setServiceSettings(settings);
        setBusinessSetup({
          ...emptyBusinessSetup,
          ...savedSetup,
          goals: Array.isArray(savedSetup.goals) ? savedSetup.goals : [],
        });
        if (!savedSetup.completed && !savedSetup.skipped) {
          setSetupOpen(true);
        }
      } catch (e) {
        if (!mounted) return;
        setServiceSettings({
          id: null,
          isBlueTickEnabled: false,
          isTypingIndicatorEnabled: false,
          isChatSimulationEnabled: false,
          allSettings: {},
        });
        setSetupOpen(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const saveBusinessSetup = async (patch = {}) => {
    const baseSettings = serviceSettings || {
      id: null,
      isBlueTickEnabled: false,
      isTypingIndicatorEnabled: false,
      isChatSimulationEnabled: false,
      allSettings: {},
    };
    const nextSetup = {
      ...businessSetup,
      ...patch,
      updatedAt: new Date().toISOString(),
    };
    setSavingSetup(true);
    try {
      const saved = await ServicesService.saveServiceSettings({
        ...baseSettings,
        allSettings: {
          ...(baseSettings.allSettings || {}),
          business_setup: nextSetup,
        },
      });
      setServiceSettings(saved);
      setBusinessSetup({
        ...emptyBusinessSetup,
        ...nextSetup,
        goals: Array.isArray(nextSetup.goals) ? nextSetup.goals : [],
      });
      window.dispatchEvent(new Event("business-setup-updated"));
      setSetupOpen(false);
    } finally {
      setSavingSetup(false);
    }
  };

  const inbox = data?.inbox || {};
  const messages = data?.messages || {};
  const contacts = data?.contacts || {};
  const automation = data?.automation || {};
  const templates = data?.templates || {};
  const periodLabel = data?.period_label || "Selected period";
  const setupCompleted = Boolean(businessSetup.completed);
  const isInitialLoad = loading && !data;
  const isRefreshing = loading && Boolean(data);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100%",
        bgcolor: dash.pageBg,
        pb: 6,
      }}
    >
      <BusinessSetupDialog
        open={setupOpen}
        value={businessSetup}
        saving={savingSetup}
        onChange={setBusinessSetup}
        onSkip={() => saveBusinessSetup({ skipped: true, completed: false })}
        onSave={() => saveBusinessSetup({ completed: true, skipped: false })}
      />

      <Box
        sx={{
          maxWidth: dash.maxContentWidth,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4, lg: 5 },
          pt: { xs: 2.5, md: 3 },
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <DashboardHero
          period={period}
          onPeriodChange={setPeriod}
          loading={loading}
          periodLabel={data?.period_label}
        />

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: 2 }}
            action={<Button onClick={load} sx={{ textTransform: "none" }}>Retry</Button>}
          >
            {error}
          </Alert>
        )}

        {isInitialLoad && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress size={40} thickness={4} sx={{ color: "primary.main" }} />
            <Typography variant="body2" color="text.secondary">
              Loading dashboard…
            </Typography>
          </Box>
        )}

        {data && (
          <Box sx={{ opacity: isRefreshing ? 0.65 : 1, transition: "opacity 0.2s ease" }}>
            <BusinessSetupRecommendations
              setup={businessSetup}
              completed={setupCompleted}
              onCustomize={() => setSetupOpen(true)}
              onNavigate={navigate}
            />

            {/* KPI row — CSS Grid for perfect equal sizing */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr 1fr",
                },
                gap: { xs: 2.5, sm: 3 },
                width: "100%",
              }}
            >
              <StatCard
                title="Open chats"
                value={inbox.conversations_with_unread ?? 0}
                subtitle={`${inbox.total_unread_messages ?? 0} unread messages`}
                icon={MarkChatUnreadIcon}
                accent="warning"
                onClick={() => navigate("/teamchat")}
              />
              <StatCard
                title="Conversations"
                value={inbox.total_conversations ?? 0}
                subtitle="Active threads (excl. simulation)"
                icon={ChatBubbleOutlineIcon}
                accent="primary"
                onClick={() => navigate("/teamchat")}
              />
              <StatCard
                title="Contacts"
                value={contacts.total_contacts ?? 0}
                subtitle={`+${contacts.new_contacts_in_period ?? 0} new · ${periodLabel}`}
                icon={ContactsIcon}
                accent="success"
                onClick={() => navigate("/contacts")}
              />
              <StatCard
                title="Automation"
                value={(automation.keyword_rules ?? 0) + (automation.chatbots ?? 0)}
                subtitle={`${automation.keyword_rules ?? 0} keywords · ${automation.chatbots ?? 0} chatbots`}
                icon={SmartToyIcon}
                accent="primary"
                onClick={() => navigate("/automation/keyword-actions")}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr", 
                  sm: "1fr 1fr",
                  md: "1fr 1fr",
                },
                gap: { xs: 2, sm: 2.5, md: 3 },
                mt: { xs: 3, sm: 3.5 },
                width: "100%",
                alignItems: "stretch",
              }}
            >
              {/* Message Volume Card */}
              <SurfaceCard
                loading={loading}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { xs: 300, md: 360 },
                  height: "100%",
                  width: "100%",
                  minWidth: 0, 
                  overflow: "hidden", 
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2, flexShrink: 0 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14, md: 16 }, letterSpacing: "-0.02em" }}>
                      Message Volume
                    </Typography>
                    <Typography variant="body2" sx={{
                      mt: 0.5,
                      color: dash.textMuted,
                      fontSize: { xs: 10, sm: 11, md: 13 }
                    }}>
                      Inbound and outbound messages · {periodLabel}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0, 
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  overflow: "hidden", 
                }}>
                  <Box sx={{
                    width: "100%",
                    overflow: "hidden", 
                  }}>
                    <MessageVolumeBars
                      series={messages.series}
                      total={messages.total_messages_period}
                      bucket={messages.bucket || "day"}
                    />
                  </Box>
                </Box>
              </SurfaceCard>

              {/* Templates & Sends Card */}
              <SurfaceCard
                loading={loading}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { xs: 300, md: 360 },
                  height: "100%",
                  width: "100%",
                  minWidth: 0, 
                }}
              >
                <Typography sx={{
                  fontWeight: 700,
                  fontSize: { xs: 13, sm: 14, md: 16 },
                  letterSpacing: "-0.02em",
                  flexShrink: 0
                }}>
                  Templates & Sends
                </Typography>
                <Typography variant="body2" sx={{
                  mt: 0.5,
                  mb: { xs: 1.5, md: 2 },
                  color: dash.textMuted,
                  fontSize: { xs: 10, sm: 11, md: 13 },
                  lineHeight: 1.55,
                  flexShrink: 0
                }}>
                  Cumulative template sends (all time). Definitions are current count for this workspace.
                </Typography>

                <Box sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}>
                  <StackedMetrics
                    rows={[
                      { label: "Template definitions", value: templates.template_definitions ?? 0, bold: true },
                      { label: "Utility sends", value: templates.sends?.utility ?? 0 },
                      { label: "Marketing sends", value: templates.sends?.marketing ?? 0 },
                      { label: "Authentication sends", value: templates.sends?.auth ?? 0 },
                    ]}
                  />
                </Box>

                <Box sx={{
                  mt: { xs: 2, md: 2.5 },
                  pt: { xs: 1.5, md: 2 },
                  borderTop: `1px solid ${dash.border}`,
                  flexShrink: 0
                }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<StyleIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
                    onClick={() => navigate("/broadcast/templates")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 1.5,
                      px: { xs: 1.5, md: 1.75 },
                      fontSize: { xs: 10, sm: 11, md: 13 },
                    }}
                  >
                    Manage Templates
                  </Button>
                </Box>
              </SurfaceCard>
            </Box>
            <Box sx={{ mt: { xs: 4, md: 5 } }}>
              <DashboardSection
                title="WhatsApp connection"
                description="Live status from your WhatsApp Business credentials."
              />
              <IntegrationStatusCard whatsapp={data?.whatsapp} />
            </Box>

            <Box sx={{ mt: { xs: 4, md: 5 } }}>
              <DashboardSection
                title="Broadcasts"
                description={`Summary for ${periodLabel}. Counts reflect campaign status in this range.`}
                action={
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => navigate("/broadcast/messages")}
                    sx={{ textTransform: "none", fontWeight: 600, px: 2, borderRadius: 1.5, boxShadow: "none" }}
                  >
                    Open broadcasts
                  </Button>
                }
              />
              <BroadcastInsightsCard insights={data?.broadcast_insights} periodLabel={periodLabel} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function BusinessSetupDialog({
  open,
  value,
  saving,
  onChange,
  onSkip,
  onSave,
}) {
  const [setupStep, setSetupStep] = useState(0);
  useEffect(() => {
    if (open) setSetupStep(0);
  }, [open]);

  const toggleGoal = (goalId) => {
    const goals = Array.isArray(value.goals) ? value.goals : [];
    const nextGoals = goals.includes(goalId)
      ? goals.filter((id) => id !== goalId)
      : [...goals, goalId];
    onChange({ ...value, goals: nextGoals });
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#fff",
      fontSize: 14,
    },
  };
  const canSave = String(value.businessName || "").trim()
    && String(value.businessPhone || "").trim()
    && (
      value.productType !== "physical-goods"
      || (
        String(value.businessAddressLine1 || "").trim()
        && String(value.businessCity || "").trim()
        && String(value.businessState || "").trim()
        && String(value.businessPincode || "").trim()
      )
    );
  const isPhysicalGoods = value.productType === "physical-goods";
  const canContinue = String(value.businessName || "").trim()
    && String(value.businessPhone || "").trim();
  const isBusinessStep = setupStep === 0;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.32)",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          py: { xs: 2.5, sm: 3 },
          background: dash.headerHeroGradient,
          color: "#fff",
        }}
      >
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Box>
            <Typography
              variant="overline"
              sx={{ color: alpha("#fff", 0.75), fontWeight: 700, letterSpacing: "0.14em" }}
            >
              Workspace setup
            </Typography>
            <DialogTitle sx={{ p: 0, mt: 0.5, fontSize: { xs: 22, sm: 26 }, fontWeight: 800 }}>
              Personalize your WhatsApp CRM
            </DialogTitle>
            <Typography sx={{ mt: 1, color: alpha("#fff", 0.84), maxWidth: 620, lineHeight: 1.55 }}>
              Choose only what matters now. We will use this to recommend the right chatbots,
              templates, broadcasts, and booking tools.
            </Typography>
          </Box>
          <Chip
            label={`Step ${setupStep + 1} of 2`}
            size="small"
            sx={{
              alignSelf: "flex-start",
              bgcolor: alpha("#fff", 0.14),
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.2)}`,
              fontWeight: 700,
            }}
          />
        </Stack>
      </Box>

      <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 }, bgcolor: "#f8fafc" }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label="1. Purpose & business"
            color={isBusinessStep ? "primary" : "default"}
            variant={isBusinessStep ? "filled" : "outlined"}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label="2. Payments & address"
            color={!isBusinessStep ? "primary" : "default"}
            variant={!isBusinessStep ? "filled" : "outlined"}
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: `1px solid ${alpha("#0f172a", 0.08)}`,
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 1.5, color: "#0f172a" }}>
            {isBusinessStep ? "Start with the basics" : "Payments and business address"}
          </Typography>
          {isBusinessStep ? (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: 16 }}>
                    What do you want to use WhatsApp for?
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 0.35 }}>
                    Select one or more. We will recommend the right tools.
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {BUSINESS_GOALS.map((goal) => {
                  const selected = (value.goals || []).includes(goal.id);
                  const Icon = goal.Icon;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={goal.id}>
                      <Box
                        onClick={() => toggleGoal(goal.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") toggleGoal(goal.id);
                        }}
                        sx={{
                          p: 1.5,
                          minHeight: 92,
                          cursor: "pointer",
                          borderRadius: 2.5,
                          border: `1px solid ${selected ? THEME_PRIMARY : alpha("#0f172a", 0.1)}`,
                          bgcolor: selected ? dash.accentSoft : "#fff",
                          boxShadow: selected ? "0 8px 24px rgba(25, 118, 210, 0.14)" : "none",
                          transition: "all 0.16s ease",
                          "&:hover": {
                            borderColor: selected ? THEME_PRIMARY : alpha(THEME_PRIMARY, 0.45),
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              bgcolor: selected ? THEME_PRIMARY : THEME_PRIMARY_LIGHT,
                              color: selected ? "#fff" : THEME_PRIMARY,
                              flexShrink: 0,
                            }}
                          >
                            <Icon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: 13.5 }}>
                              {goal.label}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25, fontSize: 12.5 }}>
                              {goal.helper}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              <Grid container spacing={1.75}>
                <Grid item xs={12} sm={4}>
                  <FieldLabel required>Business name</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    value={value.businessName || ""}
                    onChange={(e) => onChange({ ...value, businessName: e.target.value })}
                    placeholder="Your brand name"
                    required
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldLabel required>Phone number</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    value={value.businessPhone || ""}
                    onChange={(e) => onChange({ ...value, businessPhone: e.target.value })}
                    placeholder="+91 98765 43210"
                    required
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Email</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    value={value.businessEmail || ""}
                    onChange={(e) => onChange({ ...value, businessEmail: e.target.value })}
                    placeholder="hello@example.com"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Business category</FieldLabel>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={value.category || ""}
                    onChange={(e) => onChange({ ...value, category: e.target.value })}
                    sx={fieldSx}
                  >
                    {BUSINESS_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Preferred language</FieldLabel>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={value.language || "English"}
                    onChange={(e) => onChange({ ...value, language: e.target.value })}
                    sx={fieldSx}
                  >
                    {LANGUAGES.map((language) => (
                      <MenuItem key={language} value={language}>{language}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid container spacing={1.75}>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Product type</FieldLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={value.productType || "digital-goods"}
                  onChange={(e) => onChange({ ...value, productType: e.target.value })}
                  helperText="Used to prepare WhatsApp payment payloads correctly."
                  sx={fieldSx}
                >
                  <MenuItem value="digital-goods">Digital goods / services / bookings</MenuItem>
                  <MenuItem value="physical-goods">Physical goods shipped to customers</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FieldLabel>Country</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={value.country || ""}
                  onChange={(e) => onChange({ ...value, country: e.target.value })}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FieldLabel>Collect payments?</FieldLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={value.paymentNeeded || ""}
                  onChange={(e) => onChange({ ...value, paymentNeeded: e.target.value })}
                  sx={fieldSx}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="later">Maybe later</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Alert severity={isPhysicalGoods ? "info" : "success"} sx={{ borderRadius: 2 }}>
                  Business address is used by AI replies and payment compliance. For physical
                  goods, Meta also uses it as importer address when an order has no catalog.
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel required={isPhysicalGoods}>Address line 1</FieldLabel>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  value={value.businessAddressLine1 || ""}
                  onChange={(e) => onChange({ ...value, businessAddressLine1: e.target.value })}
                  placeholder="Door number, building, street"
                  required={isPhysicalGoods}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Address line 2</FieldLabel>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  value={value.businessAddressLine2 || ""}
                  onChange={(e) => onChange({ ...value, businessAddressLine2: e.target.value })}
                  placeholder="Area, landmark, nearby location"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel required={isPhysicalGoods}>City</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={value.businessCity || ""}
                  onChange={(e) => onChange({ ...value, businessCity: e.target.value })}
                  required={isPhysicalGoods}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel required={isPhysicalGoods}>State</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={value.businessState || ""}
                  onChange={(e) => onChange({ ...value, businessState: e.target.value })}
                  placeholder="Tamil Nadu"
                  required={isPhysicalGoods}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel required={isPhysicalGoods}>Pincode</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  value={value.businessPincode || ""}
                  onChange={(e) => onChange({ ...value, businessPincode: e.target.value })}
                  placeholder="600001"
                  required={isPhysicalGoods}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          )}
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.25, bgcolor: "#fff" }}>
        <Button onClick={onSkip} disabled={saving} sx={{ textTransform: "none", color: "#475569" }}>
          Skip for now
        </Button>
        <Box sx={{ flex: 1 }} />
        {!isBusinessStep && (
          <Button onClick={() => setSetupStep(0)} disabled={saving} sx={{ textTransform: "none" }}>
            Back
          </Button>
        )}
        {isBusinessStep ? (
          <Button
            variant="contained"
            onClick={() => setSetupStep(1)}
            disabled={!canContinue}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              bgcolor: THEME_PRIMARY,
              "&:hover": { bgcolor: "#1565c0" },
            }}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving || !canSave}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              px: 2.5,
              bgcolor: THEME_PRIMARY,
              "&:hover": { bgcolor: "#1565c0" },
            }}
          >
            Save setup
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function FieldLabel({ children, required = false }) {
  return (
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155", mb: 0.65 }}>
      {children}
      {required && <Box component="span" sx={{ color: "#d32f2f", ml: 0.35 }}>*</Box>}
    </Typography>
  );
}

function BusinessSetupRecommendations({
  setup,
  completed,
  onCustomize,
  onNavigate,
}) {
  const goals = Array.isArray(setup.goals) && setup.goals.length
    ? setup.goals
    : ["support", "broadcasts", "leads"];

  const cards = goals
    .map((goalId) => BUSINESS_GOALS.find((goal) => goal.id === goalId))
    .filter(Boolean)
    .slice(0, 4);

  const actionCopy = {
    sell_products: "Build product chatbot",
    appointments: "Create appointment setup",
    tickets: "Create ticket inventory",
    support: "Open team inbox",
    broadcasts: "Create broadcast",
    leads: "Build lead flow",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        mb: 3.5,
        borderRadius: 3,
        border: `1px solid ${dash.border}`,
        bgcolor: dash.surface,
        boxShadow: dash.shadowSm,
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: dash.shadowMd,
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
              color: "text.primary",
              letterSpacing: "-0.01em",
            }}
          >
            Recommended Setup
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.25,
              fontSize: 13,
            }}
          >
            {completed
              ? "Based on your WhatsApp goals. Start with these recommended workflows."
              : "Answer a few quick preferences to personalize this workspace."}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={onCustomize}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            px: 2.5,
            py: 0.75,
            borderColor: dash.border,
            color: "text.primary",
            flexShrink: 0,
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: alpha("#2563eb", 0.04),
            },
          }}
        >
          {completed ? "Edit Preferences" : "Customize Workspace"}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: cards.length <= 2 ? "1fr 1fr" : "1fr 1fr",
            md: `repeat(${Math.min(cards.length, 4)}, 1fr)`,
          },
          gap: 2,
          width: "100%",
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${dash.border}`,
              bgcolor: alpha("#f8fafc", 0.6),
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              height: "100%",
              minHeight: { xs: 110, sm: 120 }, // ← Reduced minHeight since no icons
              position: "relative",
              "&:hover": {
                borderColor: alpha("#2563eb", 0.3),
                bgcolor: alpha("#2563eb", 0.02),
                boxShadow: dash.shadowSm,
                transform: "translateY(-2px)",
              },
            }}
          >
           
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 14, 
                  color: "text.primary",
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
              >
                {actionCopy[card.id] || card.label}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: 12,
                  lineHeight: 1.4,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {recommendationDescription(card.id)}
              </Typography>
            </Box>

            <Button
              size="small"
              onClick={() => onNavigate(card.path)}
              sx={{
                mt: 1.5, // ← Reduced margin
                px: 0,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                color: "primary.main",
                alignSelf: "flex-start",
                "&:hover": {
                  bgcolor: "transparent",
                  color: "primary.dark",
                },
                "& .MuiTouchRipple-root": {
                  display: "none",
                },
              }}
            >
              Start 
            </Button>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
function recommendationDescription(goalId) {
  switch (goalId) {
    case "sell_products":
      return "Connect catalog or create a product conversation.";
    case "appointments":
      return "Add services, staff, hours, then use booking actions.";
    case "tickets":
      return "Create routes, tiers, seats, and reservation flows.";
    case "support":
      return "Manage customer replies from the shared inbox.";
    case "broadcasts":
      return "Send approved updates to the right audience.";
    case "leads":
      return "Ask simple questions and save lead details.";
    default:
      return "Set up the next WhatsApp workflow.";
  }
}

function SurfaceCard({ children, loading, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: `1px solid ${dash.border}`,
        bgcolor: dash.surface,
        boxShadow: dash.shadowSm,
        opacity: loading ? 0.72 : 1,
        transition: "opacity 0.2s ease",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function StackedMetrics({ rows }) {
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${alpha("#0f172a", 0.06)}`,
        overflow: "hidden",
      }}
    >
      {rows.map((row, i) => (
        <Box
          key={row.label}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1.5,
            py: 1.25,
            bgcolor: i % 2 === 0 ? alpha("#f8fafc", 0.9) : "transparent",
            borderBottom: i < rows.length - 1 ? `1px solid ${alpha("#0f172a", 0.06)}` : "none",
          }}
        >
          <Typography sx={{ fontSize: 13, color: dash.textMuted }}>{row.label}</Typography>
          <Typography sx={{ fontWeight: row.bold ? 700 : 600, fontSize: 13, fontFeatureSettings: '"tnum"' }}>
            {typeof row.value === "number" ? row.value.toLocaleString() : row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function LinearSkeleton() {
  return (
    <Box sx={{ width: "100%" }}>
      <Skeleton variant="rounded" height={8} sx={{ borderRadius: 1 }} />
    </Box>
  );
}
